import { BOOKING_CONTEXT } from '~/literals';
import {
  FlightBookingPostParams,
  FlightOfferPriceCheckResponse,
  Passenger,
  PassengerRequirement,
  PassengerType,
  FlightBookingPostParamsValidationMessage,
  ListTypeMessage,
  LegGroup,
} from '~/types';
import { initAssignBagsToPassenger, isValidEmail } from '~/modules/helpers';

export const getBookingPostParams = (flightBookingDetails: FlightOfferPriceCheckResponse | null): FlightBookingPostParams => {
  return {
    priceCheckId: flightBookingDetails?.priceCheckId || '',
    remarks: null,
    ticketingAgreement: null,
    tripContactPersons: [{
      id: '',
      firstName: '',
      lastName: '',
      gender: '',
      contactDetails: {
        addressLines: [''],
        postalCode: '',
        city: {
          code: '',
          friendlyName: '',
        },
        country: {
          code: '',
          friendlyName: '',
        },
        phoneNumbers: [
          {
            countryCode: '',
            type: 'MOBILE',
            number: '',
          },
        ],
        email: '',
      }
    }],
    passengers: flightBookingDetails?.response?.bookingRequirements?.passengerRequirements ? flightBookingDetails?.response.bookingRequirements.passengerRequirements.map((passenger: PassengerRequirement): Passenger => {
      let passengerType = PassengerType.NONE;

      if (flightBookingDetails.response.offers[0].passengerPricing) {
        const filtered = flightBookingDetails.response.offers[0].passengerPricing.filter(pp => pp.id === passenger.passengerId);
        passengerType = filtered.length ? filtered[0].passengerType as PassengerType : PassengerType.NONE;
      }

      const itineraries: LegGroup[] = [flightBookingDetails.response.offers[0].itineraries.outbound];
      if (flightBookingDetails.response.offers[0].itineraries.inbound) {
        itineraries.push(flightBookingDetails.response.offers[0].itineraries.inbound);
      }

      return {
        id: passenger.passengerId,
        passengerType,
        firstName: '',
        lastName: '',
        gender: '',
        dateOfBirth: '',
        documentsRequired: passenger.documentRequired,
        documents: passenger.documentRequired ?
          [{
            documentType: 'PASSPORT',
            birthPlace: '',
            issuanceLocation: '',
            issuanceDate: '',
            number: '',
            expiryDate: '',
            countryOfIssue: {
              code: '',
              friendlyName: '',
            },
            countryWhereValid: {
              code: '',
              friendlyName: '',
            },
            nationality: {
              code: '',
              friendlyName: '',
            },
            holder: true,
          }]
          : null,
        companyName: '',
        extraBaggage: flightBookingDetails?.response.upsellExtras?.bags ? initAssignBagsToPassenger(flightBookingDetails?.response.upsellExtras?.bags, itineraries, passenger.passengerId) : {},
      }
    }) : [],
    order: flightBookingDetails ? { offers: flightBookingDetails.response.offers } : null,
    source: flightBookingDetails?.response.source || '',
    termsAccepted: false,
  };
}

export const getBookingValidationMessages = (fields: FlightBookingPostParams, bookingContext: string): FlightBookingPostParamsValidationMessage => {
  const validationMessages: FlightBookingPostParamsValidationMessage = {
    tripContactPersons: [],
    passengers: [],
    globalErrorMessages: [],
    termsAccepted: [],
  };

  fields?.passengers?.map((passenger) => {
    const passengerErrrorMessages: ListTypeMessage = {
      index: Number(passenger.id),
      fields: {
        firstName: [],
        lastName: [],
        dateOfBirth: [],
        gender: [],
      },
      children: [],
    }

    if (!passenger.firstName) {
      passengerErrrorMessages.fields.firstName.push('First name is required.');
    }

    if (!passenger.lastName) {
      passengerErrrorMessages.fields.lastName.push('Surname is required.');
    }

    if (bookingContext !== BOOKING_CONTEXT.FLIGHT_ITINERARY) {
      if (!passenger.gender) {
        passengerErrrorMessages.fields.gender.push('Gender is required.');
      }

      if (!passenger.dateOfBirth) {
        passengerErrrorMessages.fields.dateOfBirth.push('Date of birth is required.');
      }

      if (passenger.dateOfBirth) {
        const dobVaildationMessage = dobValidForPassengerType(passenger);
        if (dobVaildationMessage)
          passengerErrrorMessages.fields.dateOfBirth.push(dobVaildationMessage);
      }

      if (passenger?.documentsRequired) {
        passenger?.documents?.map((doc, docIndex) => {
          const childMessage: ListTypeMessage = {
            index: docIndex,
            fields: {
              nationality: [],
              documentType: [],
              number: [],
              expiryDate: []
            }
          };

          if (!doc.nationality.code) {
            childMessage.fields.nationality.push('Nationality is required.');
          }
          if (!doc.documentType) {
            childMessage.fields.documentType.push('Travel document type is required.');
          }
          if (!doc.number) {
            childMessage.fields.number.push('Travel document number is required.');
          }
          if (!doc.expiryDate) {
            childMessage.fields.expiryDate.push('Travel document expiry date is required.');
          }
          if (doc.expiryDate && new Date(doc.expiryDate) < new Date()) {
            childMessage.fields.expiryDate.push('Travel document expired. Valid date should be in the future.');
          }
          passengerErrrorMessages.children?.push(childMessage);
        });
      }
    }

    validationMessages?.passengers?.push(passengerErrrorMessages);
  });

  const checkAdults = validAssociatedAdults(fields?.passengers || []);
  if (checkAdults) {
    validationMessages?.globalErrorMessages.push(checkAdults);
  }


  fields?.tripContactPersons?.map((contactPerson, index) => {
    const contactPersonErrrorMessages: ListTypeMessage = {
      index: index,
      fields: {
        email: [],
        number: [],
        countryCode: [],
        addressLines: [],
        city: [],
        country: [],
        postalCode: [],
      }
    }

    if (contactPerson.contactDetails?.email && !isValidEmail(contactPerson.contactDetails.email)) {
      contactPersonErrrorMessages.fields.email.push('Invalid email address.');
    }
    if (!contactPerson.contactDetails?.email) {
      contactPersonErrrorMessages.fields.email.push('Email address is required.');
    }
    if (!contactPerson.contactDetails?.phoneNumbers[0].number) {
      contactPersonErrrorMessages.fields.number.push('Mobile phone number is required.');
    }
    if (!contactPerson.contactDetails?.phoneNumbers[0].countryCode) {
      contactPersonErrrorMessages.fields.countryCode.push('Phone country code is required.');
    }

    if (bookingContext !== BOOKING_CONTEXT.FLIGHT_ITINERARY) {
      if (contactPerson.contactDetails?.addressLines && !contactPerson?.contactDetails?.addressLines[0]) {
        contactPersonErrrorMessages.fields.addressLines.push('Address is required.');
      }
      if (!contactPerson.contactDetails?.city?.friendlyName) {
        contactPersonErrrorMessages.fields.city.push('City is required.');
      }
      if (!contactPerson.contactDetails?.country?.code) {
        contactPersonErrrorMessages.fields.country.push('Country is required.');
      }
      if (!contactPerson.contactDetails?.postalCode) {
        contactPersonErrrorMessages.fields.postalCode.push('Postal/Zip code is required.');
      }
    }

    validationMessages?.tripContactPersons?.push(contactPersonErrrorMessages);
  });

  if (!fields.termsAccepted) {
    validationMessages.termsAccepted.push('You must accept the terms and conditions to continue.');
  }

  return validationMessages;
};

export function getAge(dob: Date): number {
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  const dayDiff = today.getDate() - dob.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  return age;
}

export function dobValidForPassengerType(passenger: Passenger): string {
  const age = getAge(new Date(passenger.dateOfBirth as string));

  switch (passenger.passengerType) {
    case PassengerType.ADULT:
      return age >= 12 ? "" : "Adult should be 12 years or older";
    case PassengerType.CHILD:
      return age >= 2 && age <= 11 ? "" : "Child should be between 2 and 11 years (inclusive)";
    case PassengerType.INFANT:
      return age < 2 ? "" : "Infant should be less than 2 years old";
    case PassengerType.HELD_INFANT:
      return age < 2 ? "" : "Infant should be less than 2 years old";
    default:
      return "";
  }
}

export function validAssociatedAdults(passengers: Passenger[]): string {
  if (passengers.some(p => p.passengerType === PassengerType.HELD_INFANT || p.passengerType === PassengerType.INFANT)) {
    const adults = passengers.filter(p => p.passengerType === PassengerType.ADULT && p.dateOfBirth);
    if (adults.length) {
      const adultAges = adults.map(a => getAge(new Date(a.dateOfBirth as string)));

      if (adultAges.some(age => age >= 18)) return '';
      else return "To complete your booking, please ensure that each infant or child is accompanied by an adult aged 18 years or above.";
    }
  }

  return '';
}